import type { EntityType } from "@/components/filter-cards";

/**
 * Mirrors the columns of the batch-screening XLS template:
 *   external_identifier · min_score · data_source · name ·
 *   date_of_birth · country · entity_type · identifier
 * `hits` is the output added after screening.
 */
export type BatchRow = {
  id: string;
  /** User-supplied reference (e.g. internal customer ID). */
  externalId: string;
  /** Per-row matching threshold (0–1). */
  minScore: number;
  /** Which watchlist(s) this name was screened against — a row can select more
   *  than one (e.g. ["sanctions-lists"], ["sanctions-lists", "pep-lists"]). */
  dataSource: string[];
  name: string;
  /** ISO YYYY-MM-DD; null for non-individual entities. */
  dateOfBirth: string | null;
  /** 2-letter ISO country code(s) (e.g. ["RU"], ["RU", "BY"]) — a row can be
   *  associated with more than one country. */
  country: string[];
  entityType: EntityType;
  /** Optional pre-known disambiguator: passport, registration number, etc. */
  identifier: string | null;
  /** Number of matches found when this row was screened. */
  hits: number;
  /** Live: the backend URL for this row's full match results (per-row drill-down).
   *  Absent on fixtures. */
  resultsUrl?: string;
};

export type Batch = {
  id: string;
  filename: string;
  /** ISO date `YYYY-MM-DD` of when the batch was uploaded. */
  uploadedAt: string;
  rows: BatchRow[];
  /** Live: total names screened, from the LIST endpoint (which carries no rows).
   *  The history list shows this when `rows` is empty (list level). */
  nameCount?: number;
  /** Live: true while the backend is still screening the batch (detail returns a
   *  `{ message }` placeholder). */
  processing?: boolean;
  /** Live: pre-signed S3 URL for the batch's zipped results file, from the
   *  detail endpoint's `file_urls.zip_results_url`. Absent while processing. */
  downloadUrl?: string;
  /** Live: human-readable messages when the uploaded file had invalid rows or
   *  columns, derived from the list/detail/upload endpoints' `parsing_errors`. */
  parsingErrorMessages?: string[];
};

const RUSSIA_HIGH_RISK: Batch = {
  id: "russia-001",
  filename: "russia-high-risk-individuals.xls",
  uploadedAt: "2026-05-05",
  rows: [
    {
      id: "row-001",
      externalId: "1001",
      minScore: 0.87,
      dataSource: ["sanctions-lists"],
      name: "Бурнинская Вихоря",
      dateOfBirth: "1965-03-12",
      country: ["RU"],
      entityType: "Individual",
      identifier: null,
      hits: 3,
    },
    {
      id: "row-002",
      externalId: "1002",
      minScore: 0.99,
      dataSource: ["sanctions-lists"],
      name: "Vladimir Putin",
      dateOfBirth: "1952-10-07",
      country: ["RU"],
      entityType: "Individual",
      identifier: "RUS-PASS-12345",
      hits: 5,
    },
    {
      id: "row-003",
      externalId: "1003",
      minScore: 0.95,
      dataSource: ["sanctions-lists"],
      name: "Aleksandr Lukashenko",
      dateOfBirth: "1954-08-30",
      country: ["BY"],
      entityType: "Individual",
      identifier: null,
      hits: 4,
    },
    {
      id: "row-004",
      externalId: "1004",
      minScore: 0.93,
      dataSource: ["sanctions-lists"],
      name: "Yevgeny Prigozhin",
      dateOfBirth: "1961-06-01",
      country: ["RU"],
      entityType: "Individual",
      identifier: null,
      hits: 7,
    },
    {
      id: "row-005",
      externalId: "1005",
      minScore: 0.96,
      dataSource: ["sanctions-lists"],
      name: "Wagner Group LLC",
      dateOfBirth: null,
      country: ["AE"],
      entityType: "Organization",
      identifier: "UAE-REG-887421",
      hits: 6,
    },
    {
      id: "row-006",
      externalId: "1006",
      minScore: 0.92,
      dataSource: ["sanctions-lists"],
      name: "Bashar al-Assad",
      dateOfBirth: "1965-09-11",
      country: ["SY"],
      entityType: "Individual",
      identifier: null,
      hits: 4,
    },
    {
      id: "row-007",
      externalId: "1007",
      minScore: 0.9,
      dataSource: ["pep-lists"],
      name: "Dmitry Medvedev",
      dateOfBirth: "1965-09-14",
      country: ["RU"],
      entityType: "Individual",
      identifier: null,
      hits: 4,
    },
    {
      id: "row-008",
      externalId: "1008",
      minScore: 0.84,
      dataSource: ["sanctions-lists"],
      name: "Alexei Navalny",
      dateOfBirth: "1976-06-04",
      country: ["RU"],
      entityType: "Individual",
      identifier: null,
      hits: 0,
    },
  ],
};

/** Lightweight historical entries — the rows are summarised, not full
 *  row data. Plenty for populating the recent-batches list on /batch. */
const Q1_VENDORS: Batch = {
  id: "q1-vendors",
  filename: "q1-vendor-onboarding.xls",
  uploadedAt: "2026-04-28",
  rows: [
    {
      id: "rv-001",
      externalId: "V-2031",
      minScore: 0.82,
      dataSource: ["sanctions-lists"],
      name: "Acme Holdings Ltd",
      dateOfBirth: null,
      country: ["GB"],
      entityType: "Organization",
      identifier: "GB-CH-09231147",
      hits: 0,
    },
    {
      id: "rv-002",
      externalId: "V-2032",
      minScore: 0.79,
      dataSource: ["sanctions-lists"],
      name: "Northwind Logistics SA",
      dateOfBirth: null,
      country: ["FR"],
      entityType: "Organization",
      identifier: null,
      hits: 0,
    },
    {
      id: "rv-003",
      externalId: "V-2033",
      minScore: 0.92,
      dataSource: ["sanctions-lists"],
      name: "Hellenic Maritime Group",
      dateOfBirth: null,
      country: ["GR"],
      entityType: "Organization",
      identifier: null,
      hits: 1,
    },
  ],
};

const PEP_REVIEW: Batch = {
  id: "pep-review-q2",
  filename: "pep-review-q2-2026.xls",
  uploadedAt: "2026-04-15",
  rows: [
    {
      id: "rp-001",
      externalId: "P-501",
      minScore: 0.96,
      dataSource: ["pep-lists"],
      name: "Recep Tayyip Erdoğan",
      dateOfBirth: "1954-02-26",
      country: ["TR"],
      entityType: "Individual",
      identifier: null,
      hits: 2,
    },
    {
      id: "rp-002",
      externalId: "P-502",
      minScore: 0.91,
      dataSource: ["pep-lists"],
      name: "Narendra Modi",
      dateOfBirth: "1950-09-17",
      country: ["IN"],
      entityType: "Individual",
      identifier: null,
      hits: 1,
    },
    {
      id: "rp-003",
      externalId: "P-503",
      minScore: 0.89,
      dataSource: ["pep-lists"],
      name: "Emmanuel Macron",
      dateOfBirth: "1977-12-21",
      country: ["FR"],
      entityType: "Individual",
      identifier: null,
      hits: 1,
    },
    {
      id: "rp-004",
      externalId: "P-504",
      minScore: 0.86,
      dataSource: ["pep-lists"],
      name: "Justin Trudeau",
      dateOfBirth: "1971-12-25",
      country: ["CA"],
      entityType: "Individual",
      identifier: null,
      hits: 0,
    },
  ],
};

const CUSTOMERS_MARCH: Batch = {
  id: "customers-mar-2026",
  filename: "new-customers-march-2026.xls",
  uploadedAt: "2026-03-31",
  rows: [
    {
      id: "rc-001",
      externalId: "C-9001",
      minScore: 0.82,
      dataSource: ["sanctions-lists"],
      name: "Maria González",
      dateOfBirth: "1985-07-22",
      country: ["ES"],
      entityType: "Individual",
      identifier: null,
      hits: 0,
    },
    {
      id: "rc-002",
      externalId: "C-9002",
      minScore: 0.79,
      dataSource: ["sanctions-lists"],
      name: "Yuki Tanaka",
      dateOfBirth: "1990-11-03",
      country: ["JP"],
      entityType: "Individual",
      identifier: null,
      hits: 0,
    },
  ],
};

const BATCHES: Batch[] = [RUSSIA_HIGH_RISK, Q1_VENDORS, PEP_REVIEW, CUSTOMERS_MARCH];

export function getBatch(id: string): Batch | undefined {
  return BATCHES.find((b) => b.id === id);
}

/** All batches, sorted by upload date (newest first). */
export function getAllBatches(): Batch[] {
  return [...BATCHES].sort((a, b) =>
    a.uploadedAt < b.uploadedAt ? 1 : a.uploadedAt > b.uploadedAt ? -1 : 0,
  );
}

/** The most recently uploaded batch — used as the destination for the upload-modal stub. */
export const LATEST_BATCH_ID = RUSSIA_HIGH_RISK.id;
